// Imports for stores, components, and utilities
import { useResult } from "../../../shared/formContext/hooks/useResult";
import Box from "../../../components/Box/Box";
import Table from "../../../components/Table/Table";
import {utils} from "../../../helpers";
import {useEffect, useState} from "react";
import {__} from "@wordpress/i18n";

// Constants for UI messages
const MESSAGES = {
    headerTitle: __("Calendarização da amortização", "df-simulators"),
    month: __("Mês", "df-simulators"),
    payment: __("Prestação", "df-simulators"),
    capitalPaid: __("Capital Pago", "df-simulators"),
    interestPaid: __("Juros Pagos", "df-simulators"),
    debt: __("Dívida", "df-simulators"),
    firstRowTitle: __("Todos", "df-simulators")
};

/**
 * Generates table data for amortization schedule.
 *
 * @param {Array} data - Raw data for the table.
 * @returns {Array} - Formatted data for the table.
 */
const generateAmortizationTableData = (data) => {
    if (!data) return [];
    return data.map((item) => [
        {
            value: utils.transformDate(item.date, false, true, '-'),
            className: "text-left",
        },
        {
            value: item.payment_month_number,
        },
        {
            value: utils.toEuro(item.capital_paid),
        },
        {
            value: utils.toEuro(item.interest_paid),
        },
        {
            value: utils.toEuro(item.remaining_balance),
        },
    ]);
};

/**
 * Main component for displaying the mortgage payment table within a box.
 *
 * @component
 * @returns {JSX.Element} - Rendered MortgagePaymentFooter component.
 */
const MortgagePaymentFooter = () => {
    const { resultData } = useResult();
    const {response, loading} = resultData;
    const [tableData, setTableData] = useState({});

    // Updates table data when response changes
    useEffect(() => {
        const table = response?.data?.result?.installments || {};
        if (Object.keys(table).length) {
            setTableData(table);
        }
    }, [response]);

    return (
        (Object.keys(tableData).length !== 0) &&
        <div className="mortgage-payment-footer">
            <h3 className={"mt-12 mb-10"}>{MESSAGES.headerTitle}</h3>
                {Object.keys(tableData).map((key) => (
                    <AmortizationTable
                        key={key}
                        year={key}
                        total={tableData[key].remaining_balance}
                        data={tableData[key].installments || {}}
                        isOpen={parseInt(key) === new Date().getFullYear()}
                    />
                ))}
        </div>
    );
};

/**
 * Component to render an amortization table for a specific year.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.year - Year of the table data.
 * @param {number} props.total - Total amount for the year.
 * @param {Object} props.data - Data for the table rows.
 * @param {boolean} props.isOpen - Whether the table is initially open.
 * @returns {JSX.Element} - Rendered AmortizationTable component.
 */
const AmortizationTable = ({year, total, data, isOpen}) => {
    const headers = [
        {value: MESSAGES.month, className: "text-left"},
        {value: MESSAGES.payment},
        {value: MESSAGES.capitalPaid},
        {value: MESSAGES.interestPaid},
        {value: MESSAGES.debt},
    ];

    const flattened: any[] = [];
    const firstRow: any[] = [
        {value: MESSAGES.firstRowTitle},
        {value: data[0]?.installment_n + '-' + data[data.length - 1]?.installment_n},
        {value: utils.toEuro(data.reduce((acc: number, item) => Math.round((acc + item.capital_paid)*100)/100, 0.00), 2)},
        {value: utils.toEuro(data.reduce((acc: number, item) => Math.round((acc + item.interest_paid)*100)/100, 0.00), 2)},
        {value: utils.toEuro(total)},
    ];
    Object.values(data).forEach((eachMonth) => {
        const installment = [
            {value: utils.transformDate(eachMonth.date, false, true, '-')},
            {value: eachMonth.installment_n},
            {value: utils.toEuro(eachMonth.capital_paid, 2)},
            {value: utils.toEuro(eachMonth.interest_paid, 2)},
            {value: utils.toEuro(eachMonth.remaining_balance, 2)},
        ];
        flattened.push(installment);
    });
    return (
        <Box
            collapsible={{open: isOpen}}
            noPadding
            header={{
                title: year,
                info: utils.toEuro(total),
            }}
        >
            <Table headers={headers} firstRow={firstRow} data={flattened}/>
        </Box>
    );
};

export default MortgagePaymentFooter;
